/**
 * Parses Instantly-export CSVs (Estage format) into normalized lead rows.
 * Used by the Tryout-Lead-Listen upload flow.
 */
import Papa from 'papaparse';

export interface ParsedLead {
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  location: string | null;
  bundesland: string | null;
  berufsgruppe: string | null;
  personalization_original: string;
}

export interface CsvParseResult {
  rows: ParsedLead[];
  errors: string[];
  skippedCount: number;
  detectedCampaignName: string | null;
  totalRowsInFile: number;
}

const REQUIRED_COLUMNS = ['E-Mail', 'Vorname', 'Nachname', 'companyName', 'personalization'] as const;

export function parseLeadCsv(csvText: string): CsvParseResult {
  const stripped = csvText.replace(/^﻿/, '');

  const parsed = Papa.parse<Record<string, string>>(stripped, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim(),
    delimiter: '', // auto-detect , and ;
    delimitersToGuess: [',', ';', '\t'],
  });

  const rawHeader = (parsed.meta.fields ?? []).map((f) => f.trim());
  const errors: string[] = [];

  const missing = REQUIRED_COLUMNS.filter((c) => !rawHeader.includes(c));
  if (missing.length > 0) {
    return {
      rows: [],
      errors: [`Pflicht-Spalte(n) fehlen: ${missing.join(', ')}`],
      skippedCount: 0,
      detectedCampaignName: null,
      totalRowsInFile: parsed.data.length,
    };
  }

  let detectedCampaignName: string | null = null;
  let skippedCount = 0;
  const rows: ParsedLead[] = [];

  for (const raw of parsed.data) {
    const get = (key: string) => (raw[key] ?? '').trim();

    const email = get('E-Mail');
    const first_name = get('Vorname');
    const last_name = get('Nachname');
    const company = get('companyName');
    const personalization_original = get('personalization');

    if (!email || !company || !personalization_original) {
      skippedCount++;
      continue;
    }

    if (!detectedCampaignName) {
      const cn = get('Kampagnenname');
      if (cn) detectedCampaignName = cn;
    }

    rows.push({
      email,
      first_name,
      last_name,
      company,
      location: get('location') || null,
      bundesland: get('custom1') || null,
      berufsgruppe: get('custom2') || null,
      personalization_original,
    });
  }

  return {
    rows,
    errors,
    skippedCount,
    detectedCampaignName,
    totalRowsInFile: parsed.data.length,
  };
}

export async function readFileAsText(file: File): Promise<string> {
  return await file.text();
}
